import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { getVaultFoldersByOwner } from '../store/slices/vaultFolderSlice';
import type { SelectedTableInfo } from '../pages/Workspace';

interface FolderItem {
  id: string;
  name: string;
}

interface TableItem {
  id: string;
  name: string;
  folderId: string;
}

export interface OpenTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Açık olan tablo (vault) id'leri – bunlar gri ve tıklanamaz gösterilir */
  alreadyOpenTableIds: string[];
  /** Kullanıcı bir tablo seçip şifreyi doğruladığında. Hata fırlatılırsa modal hata mesajını gösterir. */
  onOpenTable: (info: SelectedTableInfo, vaultMasterPassword: string) => Promise<void>;
}

const OpenTableModal: React.FC<OpenTableModalProps> = ({
  isOpen,
  onClose,
  alreadyOpenTableIds,
  onOpenTable,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.auth.user?.id);
  const vaultFolderItems = useAppSelector((state) => state.vaultFolder.items);
  const listLoading = useAppSelector((state) => state.vaultFolder.operationLoading.list);

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [tableToOpen, setTableToOpen] = useState<SelectedTableInfo | null>(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      dispatch(getVaultFoldersByOwner(userId));
    }
  }, [isOpen, userId, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFolderId(null);
      setTableToOpen(null);
      setPassword('');
      setPasswordError(null);
    }
  }, [isOpen]);

  const folders: FolderItem[] = vaultFolderItems.map((item) => ({
    id: item.id,
    name: item.name ?? '',
  }));

  const selectedFolderItem = selectedFolderId
    ? vaultFolderItems.find((f) => f.id === selectedFolderId)
    : null;
  const selectedFolder = selectedFolderId ? folders.find((f) => f.id === selectedFolderId) : null;
  const tablesInFolder: TableItem[] = (selectedFolderItem?.vaults ?? []).map((v) => ({
    id: v.id,
    name: v.name ?? '',
    folderId: v.folderId ?? selectedFolderId ?? '',
  }));

  const handleTableClick = (table: TableItem) => {
    if (alreadyOpenTableIds.includes(table.id)) return;
    setTableToOpen({
      tableId: table.id,
      tableName: table.name,
      folderId: table.folderId,
      folderName: selectedFolder?.name ?? '',
    });
    setPassword('');
    setPasswordError(null);
  };

  const handleConfirmOpen = async () => {
    if (!tableToOpen || !password.trim()) {
      setPasswordError(t('pages.workspace.vaultPasswordRequiredForAll'));
      return;
    }
    setPasswordError(null);
    setIsChecking(true);
    try {
      await onOpenTable(tableToOpen, password.trim());
      setTableToOpen(null);
      setPassword('');
      onClose();
    } catch {
      setPasswordError(t('pages.workspace.vaultPasswordWrong', 'Şifre hatalı. Lütfen tekrar deneyin.'));
    } finally {
      setIsChecking(false);
    }
  };

  const handleCancelOpen = () => {
    setTableToOpen(null);
    setPassword('');
    setPasswordError(null);
  };

  if (!isOpen) return null;

  const content = (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={onClose} />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
        <div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 dark:border-gray-700/80 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('pages.passwords.openTableModalTitle', 'Vault aç')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('pages.passwords.openTableModalDescription', 'Açmak istediğiniz vault\'u seçin. Zaten açık olanlar gri gösterilir.')}
            </p>
          </div>

          <div className="flex flex-1 min-h-0">
            {/* Klasörler */}
            <div className="w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
              <div className="p-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  {t('pages.workspace.folders')}
                </h4>
                {listLoading ? (
                  <div className="flex items-center justify-center py-6 text-gray-500 dark:text-gray-400">
                    <i className="fa-solid fa-spinner fa-spin mr-2" />
                    <span className="text-sm">{t('common.loading', 'Yükleniyor...')}</span>
                  </div>
                ) : folders.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                    {t('pages.workspace.noFolders', 'Henüz klasör yok.')}
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {folders.map((folder) => {
                      const isSelected = selectedFolderId === folder.id;
                      return (
                        <li key={folder.id}>
                          <button
                            type="button"
                            onClick={() => setSelectedFolderId(folder.id)}
                            className={`
                              w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left text-sm font-medium transition-colors
                              ${isSelected
                                ? 'bg-primary text-white'
                                : 'bg-gray-50 dark:bg-gray-700/40 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                              }
                            `}
                          >
                            <i className="fa-solid fa-folder text-base flex-shrink-0" />
                            <span className="truncate">{folder.name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* Tablolar */}
            <div className="flex-1 flex flex-col min-w-0 p-4 overflow-y-auto">
              {selectedFolderId ? (
                tablesInFolder.length > 0 ? (
                  <>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                      {t('pages.workspace.tablesInFolder', { folder: selectedFolder?.name ?? '' })}
                    </h4>
                    <ul className="grid gap-3 sm:grid-cols-2">
                      {tablesInFolder.map((table) => {
                        const isAlreadyOpen = alreadyOpenTableIds.includes(table.id);
                        const isSelectedForOpen = tableToOpen?.tableId === table.id;
                        return (
                          <li key={table.id}>
                            <div
                              className={`
                                rounded-xl border-2 p-4 transition-all duration-200
                                ${isAlreadyOpen
                                  ? 'border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700/50 opacity-70 cursor-not-allowed'
                                  : isSelectedForOpen
                                    ? 'border-primary bg-primary/10 dark:bg-primary/15'
                                    : 'border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/30 hover:border-gray-300 dark:hover:border-gray-500 cursor-pointer'
                                }
                              `}
                            >
                              <button
                                type="button"
                                onClick={() => handleTableClick(table)}
                                disabled={isAlreadyOpen}
                                className="flex items-center gap-3 w-full text-left min-w-0"
                              >
                                <div
                                  className={`
                                    flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                                    ${isAlreadyOpen
                                      ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                                      : isSelectedForOpen
                                        ? 'bg-primary text-white'
                                        : 'bg-white dark:bg-gray-600/50 text-gray-500 dark:text-gray-400'
                                    }
                                  `}
                                >
                                  {isAlreadyOpen ? (
                                    <i className="fa-solid fa-check text-sm" />
                                  ) : (
                                    <i className="fa-solid fa-table-cells text-sm" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="font-semibold block truncate text-gray-800 dark:text-gray-200">
                                    {table.name}
                                  </span>
                                  <span
                                    className={`
                                      text-xs block mt-0.5
                                      ${isAlreadyOpen
                                        ? 'text-gray-500 dark:text-gray-400'
                                        : 'text-gray-500 dark:text-gray-400'
                                      }
                                    `}
                                  >
                                    {isAlreadyOpen
                                      ? t('pages.passwords.alreadyOpen', 'Zaten açık')
                                      : t('pages.workspace.clickToSelect')}
                                  </span>
                                </div>
                              </button>
                              {isSelectedForOpen && (
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('pages.workspace.vaultPasswordPlaceholder')}
                                  </label>
                                  <input
                                    type="text"
                                    value={password}
                                    onChange={(e) => {
                                      setPassword(e.target.value);
                                      setPasswordError(null);
                                    }}
                                    onKeyDown={(e) => e.key === 'Enter' && handleConfirmOpen()}
                                    placeholder={t('pages.workspace.vaultPasswordPlaceholder')}
                                    autoComplete="off"
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 text-sm input-mask-password"
                                  />
                                  {passwordError && (
                                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                      <i className="fa-solid fa-circle-exclamation" />
                                      {passwordError}
                                    </p>
                                  )}
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={handleCancelOpen}
                                      className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
                                    >
                                      {t('common.cancel')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleConfirmOpen}
                                      disabled={isChecking || !password.trim()}
                                      className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                                    >
                                      {isChecking ? (
                                        <>
                                          <i className="fa-solid fa-spinner fa-spin" />
                                          {t('common.loading', 'Yükleniyor...')}
                                        </>
                                      ) : (
                                        t('pages.workspace.open')
                                      )}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      {t('pages.workspace.noTables')}
                    </p>
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {t('pages.workspace.selectFolderHint')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700/80 flex justify-end flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors font-medium"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
  return createPortal(content, document.body);
};

export default OpenTableModal;
